import {
  Avatar,
  Badge,
  ColumnGroup,
  CustomColumnDef,
  DataTable,
  Dropdown,
  Icons,
} from '@commenda/kakashi';
import { GovernanceRole, IPersonOfBusinessEntity } from '@commenda/types';
import { formatPersonName } from '@commenda/utils';
import { useMemo, useState } from 'react';
import { isOwnershipOverhaulOn } from '../OwnershipV2/featureFlag';
import { getKeyPersonRoles, KEY_PERSON_ROLES_ENUM_TO_TEXT, KeyPersonRolesEnum } from './utils';

export const KeyPersonsTable = ({
  companyId,
  onAddKeyPerson,
  onDeleteKeyPerson,
  keyPersons,
  onViewKeyPerson,
  isEditable,
  shouldShowViewKeyPersonButton = true,
  disabled = false,
}: {
  companyId: number;
  onAddKeyPerson: (keyPersonId: number) => void;
  onDeleteKeyPerson: (keyPersonId: number) => void;
  onViewKeyPerson: (keyPersonId: number) => void;
  keyPersons: IPersonOfBusinessEntity[];
  isEditable: boolean;
  shouldShowViewKeyPersonButton?: boolean;
  disabled?: boolean;
}) => {
  const [activeFilters, setActiveFilters] = useState<
    Record<string, string | string[]>
  >({});

  // UBO badge surfaces a role only computed by the OwnershipV2 BE pipeline.
  // Hide it under the flag so flag-off matches pre-PR behaviour.
  const showUboBadge = isOwnershipOverhaulOn(companyId);

  type KeyPerson = {
    name: string;
    email: string;
    id: number;
    roles: string[];
    isAuthorizedSignatory: boolean;
    isBeneficialOwner: boolean;
  };

  const formattedKeyPersons: KeyPerson[] = useMemo(
    () =>
      keyPersons.map((person) => ({
        name: formatPersonName(person),
        email: person.email,
        id: person.id,
        roles: getKeyPersonRoles(person),
        isAuthorizedSignatory:
          !!person.governanceRelationships?.[
            GovernanceRole.AUTHORIZED_SIGNATORY
          ]?.isAuthorizedSignatory,
        isBeneficialOwner:
          !!person.governanceRelationships?.[
            GovernanceRole.BENEFICIAL_OWNER
          ]?.isBeneficialOwner,
      })),
    [keyPersons]
  );

  const filteredKeyPersons = useMemo(() => {
    const roleFilters = activeFilters.roles;

    if (
      !roleFilters ||
      (Array.isArray(roleFilters) && roleFilters.length === 0)
    ) {
      return formattedKeyPersons;
    }

    const filterValues = Array.isArray(roleFilters)
      ? roleFilters
      : [roleFilters];

    return formattedKeyPersons.filter((person) => {
      const personRoles = person.roles || [];
      return filterValues.some((filterVal) =>
        personRoles.some(
          (role) =>
            role === filterVal || role.toLowerCase() === filterVal.toLowerCase()
        )
      );
    });
  }, [formattedKeyPersons, activeFilters]);

  const columns: CustomColumnDef<KeyPerson, unknown>[] = useMemo(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        title: 'Name',
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-lg min-w-[320px] py-md">
              <Avatar size="md" name={row.original.name} />
              <div className="flex flex-col w-full">
                <span className="txt-small-medium">{row.original.name}</span>
                <span className="txt-small-regular text-text-tertiary">
                  {row.original.email}
                </span>
              </div>
            </div>
          );
        },
        sort: {
          shouldSort: true,
          ui: {
            asc: {
              columnNode: 'Sort alphabetically: A–Z',
              globalNode: 'Name: A–Z',
            },
            desc: {
              columnNode: 'Sort alphabetically: Z–A',
              globalNode: 'Name: Z–A',
            },
          },
          sortingFn: () => {
            return 1;
          },
        },
      },
      {
        id: 'roles',
        accessorKey: 'roles',
        header: 'Role',
        title: 'Role',
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-xs">
              {row.original.roles.map((role) => (
                <Badge key={role} variant="gray" size="sm">
                  {KEY_PERSON_ROLES_ENUM_TO_TEXT[role] || role}
                </Badge>
              ))}
              {row.original.isAuthorizedSignatory && (
                <Badge variant="gray" size="sm">
                  Authorized Signatory
                </Badge>
              )}
              {showUboBadge && row.original.isBeneficialOwner && (
                <Badge variant="blue" size="sm">
                  UBO
                </Badge>
              )}
            </div>
          );
        },
      },
    ],
    [showUboBadge]
  );

  const actionsColumn: CustomColumnDef<KeyPerson, unknown> = useMemo(
    () => ({
      id: 'actions',
      accessorKey: 'actions',
      header: '',
      title: '',
      width: 84,
      cell: ({ row }) => {
        if (!isEditable && !shouldShowViewKeyPersonButton) return null;

        return (
          <div className="w-full flex justify-end">
            <Dropdown
              menu={{
                groups: [
                  {
                    items: [
                      ...(shouldShowViewKeyPersonButton
                        ? [
                            {
                              label: 'View details',
                              icon: Icons.Eye,
                              onClick: () => {
                                onViewKeyPerson(row.original.id);
                              },
                            },
                          ]
                        : []),
                      ...(isEditable && !disabled
                        ? [
                            {
                              label: 'Edit details',
                              icon: Icons.Edit02,
                              onClick: () => {
                                onAddKeyPerson(row.original.id);
                              },
                            },
                            {
                              label: 'Delete',
                              icon: Icons.Trash01,
                              onClick: () => {
                                onDeleteKeyPerson(row.original.id);
                              },
                              labelClassName: 'text-utility-error-600',
                              iconClassName: 'text-utility-error-600',
                            },
                          ]
                        : []),
                    ],
                  },
                ],
              }}
              align="end"
            >
              <div className="size-8 flex justify-center items-center group -translate-x-4">
                <Icons.DotsVertical className="size-4 text-fg-quaternary group-hover:text-fg-quaternary-hover" />
              </div>
            </Dropdown>
          </div>
        );
      },
    }),
    [
      isEditable,
      shouldShowViewKeyPersonButton,
      disabled,
      onViewKeyPerson,
      onAddKeyPerson,
      onDeleteKeyPerson,
    ]
  );

  const columnGroups: ColumnGroup<KeyPerson, unknown>[] = useMemo(
    () => [
      { id: 'main', columns },
      {
        id: 'actions-column',
        sticky: 'right',
        columns: [actionsColumn],
      },
    ],
    [columns, actionsColumn]
  );

  return (
    <DataTable
      onRowClick={(row) => onViewKeyPerson(row.id)}
      columnGroups={columnGroups}
      data={filteredKeyPersons}
      paginationConfig={{
        isEnabled: true,
        pageSize: 10,
        type: 'PAGE',
      }}
      searchConfig={{
        isEnabled: true,
        placeholder: 'Search key persons',
        columnsToEnableSearchFor: ['name', 'email', 'roles'],
      }}
      columnSelectorConfig={{
        isEnabled: true,
        placeholder: 'Customise',
        defaultVisibleColumns: ['name', 'roles'],
      }}
      tableViewConfig={{
        isEnabled: true,
        defaultView: 'expanded',
      }}
      tableClassName="[&_tr:not(:last-child)]:border-b"
      filterConfig={{
        filters: [
          {
            columnId: 'roleFilter',
            icon: <Icons.User01 className="size-4" />,
            type: 'MULTI_SELECT',
            label: 'Role',
            options: [
              {
                label: 'Director',
                value: KeyPersonRolesEnum.DIRECTOR,
              },
              {
                label: 'President',
                value: KeyPersonRolesEnum.PRESIDENT,
              },
              {
                label: 'Secretary',
                value: KeyPersonRolesEnum.SECRETARY,
              },
              {
                label: 'Treasurer',
                value: KeyPersonRolesEnum.TREASURER,
              },
            ],
          },
        ],
        activeFilters: activeFilters.roles
          ? { roleFilter: activeFilters.roles }
          : {},
        onFilterChange: (filters) => {
          setActiveFilters(
            filters.roleFilter ? { roles: filters.roleFilter } : {}
          );
        },
      }}
    />
  );
};
